import { useEffect, useRef } from 'react';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { authService } from '@/shared/api';
import { useInvalidateUserData } from '@/shared/hooks/use-user-data';
import { analytics } from '@/shared/lib/analytics';
import { buildGhostUser, getGhostKind } from '@/shared/lib/dev-ghost';
import { logger } from '@/shared/lib/logger';
import { supabaseAuthService } from '@/shared/services/supabase-auth-service';

type AuthProviderProps = {
  children: React.ReactNode;
};

/**
 * Outcome of an /auth/me fetch. We distinguish a TRANSIENT failure (network
 * blip, 5xx, slow/saturated API, malformed proxy body) from an EXPLICIT
 * 'unauthenticated' (401 / authenticated:false). Only the latter ends a
 * session; a transient failure on a valid Supabase session is retried so the
 * user isn't bounced to /auth on reload (the reload-logout incident).
 */
type AuthOutcome = 'authenticated' | 'unauthenticated' | 'transient';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const login = useAuthStore((state) => state.login);
  const resetAuthState = useAuthStore((state) => state.resetAuthState);
  const setInitializing = useAuthStore((state) => state.setInitializing);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isHandlingAuthRef = useRef(false); // Use ref to persist across re-renders
  // One-shot lock that prevents the missing-team self-heal from looping if
  // /auth/register itself silently no-ops (e.g. RLS denies the team insert,
  // a downstream trigger fails). Without this, every getMe → still no team
  // would re-fire register and we'd hammer the API.
  const hasAttemptedTeamHealRef = useRef(false);

  // Use cached user data hook
  const { invalidateUserData, setUserData, removeUserData } = useInvalidateUserData();

  // Helper function to fetch user data with caching.
  // shouldIdentify: true only for initial session (when user first visits with existing session).
  // Returns the AUTHORITATIVENESS of the result (see AuthOutcome), so the
  // initial-session flow can retry a transient /auth/me on a valid session
  // instead of stranding the user logged-out.
  const fetchUserDataWithCache = async (
    shouldIdentify: boolean = false,
  ): Promise<AuthOutcome> => {
    try {
      const response = await authService.getMe(); // Use normal flow with tokens

      if (response?.data && 'id' in response.data) {
        logger.auth.debug('User authenticated successfully', { userId: response.data.id });

        // Self-heal: if Supabase auth says we're signed in but the backend
        // sees no team, the original signup's POST /auth/register never landed
        // (CORS on first attempt, stale Vite bundle, transient network) — auth
        // succeeded but the profile/team rows never got created. Symptoms the
        // founder hit: 0 free credits in the wizard, Generate → 400
        // "User does not belong to any team".
        //
        // auth.service.register() is fully idempotent (profile upsert with
        // ignoreDuplicates so onboarding state survives, teamService.create
        // returns the existing team if one is already there), so retrying it
        // is safe. We do this BEFORE login() so the store never observes the
        // intermediate "authenticated but teamless" state.
        if (!response.data.hasTeam && !hasAttemptedTeamHealRef.current) {
          hasAttemptedTeamHealRef.current = true;
          logger.auth.warn('User authenticated but has no team — running /auth/register self-heal', {
            userId: response.data.id,
          });
          try {
            await authService.registerOAuthUser();
            const refreshed = await authService.getMe();
            if (refreshed?.data && 'id' in refreshed.data) {
              logger.auth.info('Self-heal complete — user now has team', {
                userId: refreshed.data.id,
                hasTeam: refreshed.data.hasTeam,
              });
              login(refreshed.data);
              setUserData(refreshed.data);
              if (shouldIdentify) {
                analytics.identify(refreshed.data.id, { email: refreshed.data.email });
              }
              return 'authenticated';
            }
          } catch (healErr) {
            // Non-fatal — fall through to logging in with the un-healed user.
            // The wizard's defensive guard (creator-first-action) will surface
            // the issue to the user with a retry CTA.
            logger.auth.error('Self-heal /auth/register retry failed', { error: healErr });
          }
        }

        login(response.data);
        setUserData(response.data); // Cache the data

        // Identify user in RudderStack only for initial session
        // Login events (email OTP, OAuth) handle identify separately
        if (shouldIdentify) {
          analytics.identify(response.data.id, { email: response.data.email });
        }

        return 'authenticated';
      } else if (response?.data && response.data.authenticated === false) {
        logger.auth.debug('Backend says not authenticated');
        resetAuthState();
        removeUserData(); // Clear cache
        return 'unauthenticated';
      } else {
        // Unexpected/malformed shape (proxy hiccup, partial body). With a valid
        // Supabase session this must NOT hard-logout — treat as transient so the
        // initial-session flow retries instead of bouncing the user to /auth.
        logger.auth.error('Unexpected response format from /auth/me', { response });
        return 'transient';
      }
    } catch (error) {
      // Distinguish transient failures (network blip, server 5xx, CORS hiccup)
      // from explicit "you are not authenticated" (401 / no session). For the
      // former we want to keep the existing user state — clearing it would
      // cascade into a /auth redirect plus follow-up "Network error" toast
      // even though the user is fine. The 401 path is already handled by
      // axios's interceptor (refresh + handleAuthFailure → signOut), so by
      // the time we land here on a 401 the session is already cleared.
      const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
      const statusCode = (error as { statusCode?: number } | null | undefined)?.statusCode;
      const isExplicitAuthFailure = status === 401 || statusCode === 401;

      if (isExplicitAuthFailure) {
        logger.auth.error('Failed to fetch user data (explicit auth failure)', { error });
        resetAuthState();
        removeUserData(); // Clear cache
        return 'unauthenticated';
      }

      // Transient — keep the existing auth state so the user doesn't get
      // bounced to /auth and then cascaded into a "Network error" loop.
      // The initial-session flow retries this; if the failure was real we'll
      // learn about it then with proper signal.
      logger.auth.warn(
        'Failed to fetch user data (transient — keeping existing auth state)',
        { error },
      );
      return 'transient';
    }
  };

  useEffect(() => {
    let mounted = true; // Track if component is still mounted
    let initialCheckComplete = false; // Track if initial check is done

    // Set initializing to true at the start
    logger.auth.debug('AuthProvider useEffect: Setting initializing to true');
    setInitializing(true);

    // DEV-ONLY ghost mode (shared/lib/dev-ghost, entered via /dev/enter):
    // seed a fake user and skip the entire Supabase session lifecycle —
    // no timers, no listeners, nothing to clobber the ghost. The whole
    // branch is dead code in production builds (import.meta.env.DEV).
    if (import.meta.env.DEV) {
      const ghost = getGhostKind();
      if (ghost) {
        logger.auth.debug(`DEV ghost mode active: ${ghost}`);
        useAuthStore.getState().login(buildGhostUser(ghost));
        useAuthStore.getState().setInitializing(false);
        return;
      }
    }

    // Absolute failsafe — only fires if the initial check truly hangs (e.g. a
    // never-resolving /auth/me on a stalled connection). Raised from 10s to 30s
    // so a SLOW-but-successful /auth/me (the API was momentarily saturated — the
    // reload-logout incident) completes and logs the user in BEFORE this strands
    // them logged-out. checkInitialSession clears it as soon as it resolves, so
    // the only cost of a higher ceiling is a longer spinner in a genuine hang.
    const initTimeout = setTimeout(async () => {
      if (mounted) {
        logger.auth.warn('Auth initialization timeout - forcing completion');
        setInitializing(false);
        isHandlingAuthRef.current = false;
        initialCheckComplete = true;
      }
    }, 30000);

    // Immediately check current session
    const checkInitialSession = async () => {
      try {
        logger.auth.debug('Checking initial session on mount');
        const { session } = await supabaseAuthService.getSession();

        if (!mounted) return;

        if (session) {
          logger.auth.debug('Initial session found, fetching user data');
          // Call identify for initial session - user visiting with existing session
          let outcome = await fetchUserDataWithCache(true);

          // A valid session paired with a non-authoritative /auth/me result
          // (network blip, 5xx, slow/saturated API, malformed body) must NOT
          // strand the user logged-out — that's the reload-logout bug. Refresh
          // the token once, then retry a few times with backoff. We stay on the
          // init spinner the whole time (isInitializing is still true), so the
          // user never sees a flash of /auth. Only an EXPLICIT 'unauthenticated'
          // (401 / authenticated:false) ends the session — and it exits this
          // block immediately without retrying.
          if (outcome === 'transient') {
            logger.auth.debug('Initial auth inconclusive, attempting token refresh');
            try {
              const { session: refreshSession } = await supabaseAuthService.refreshSession();
              if (mounted && refreshSession) {
                logger.auth.debug('Token refreshed during initial check, retrying /auth/me');
                outcome = await fetchUserDataWithCache(true);
              }
            } catch (refreshErr) {
              logger.auth.error('Token refresh failed during initial check', { error: refreshErr });
            }
          }

          for (let attempt = 0; mounted && outcome === 'transient' && attempt < 3; attempt += 1) {
            await new Promise((resolve) => {
              setTimeout(resolve, 1500);
            });
            if (!mounted) break;
            logger.auth.debug('Retrying /auth/me after transient failure', { attempt: attempt + 1 });
            outcome = await fetchUserDataWithCache(true);
          }

          if (outcome === 'transient') {
            // Sustained backend outage across every retry. We hold a valid
            // session but no profile data, so the route gates can't be
            // populated and init completes without a user (→ /auth). This is far
            // rarer than the single-slow-request reload we're fixing, and the
            // next navigation retries cleanly.
            logger.auth.warn('Initial /auth/me still failing after retries — completing init without user');
          }
        } else {
          logger.auth.debug('No initial session found');
          resetAuthState();
        }

        // Set initializing to false after initial check
        if (mounted) {
          logger.auth.debug('Initial session check complete, setting initializing to false');
          setInitializing(false);
          initialCheckComplete = true;
          clearTimeout(initTimeout);
        }
      } catch (error) {
        logger.auth.error('Error checking initial session', { error });
        if (mounted) {
          setInitializing(false);
          initialCheckComplete = true;
          clearTimeout(initTimeout);
        }
      }
    };

    // Run initial session check
    checkInitialSession();

    // Listen for auth state changes (login, logout, token refresh, initial session)
    const unsubscribe = supabaseAuthService.onAuthStateChange(async (event, session) => {
      logger.auth.debug('Auth state changed', {
        event,
        hasSession: !!session,
        initialCheckComplete,
      });

      // Skip events during initial check
      if (!initialCheckComplete) {
        logger.auth.debug('Initial check not complete, skipping event', { event });
        return;
      }

      // Skip INITIAL_SESSION event as we handle it manually above
      if (event === 'INITIAL_SESSION') {
        logger.auth.debug('Skipping INITIAL_SESSION event - already handled on mount');
        return;
      }

      // Prevent handling multiple events simultaneously
      if (isHandlingAuthRef.current && event !== 'SIGNED_OUT') {
        logger.auth.debug('Already handling auth event, skipping', { event });
        return;
      }

      // Clear existing refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }

      // Handle auth events - only fetch user data when really necessary
      if (event === 'SIGNED_IN') {
        // Only SIGNED_IN requires immediate user data fetch
        if (session) {
          isHandlingAuthRef.current = true;

          // Set up proactive token refresh every 5 minutes (reduced frequency)
          // This ensures tokens are refreshed well before they expire
          if (!refreshIntervalRef.current) {
            logger.auth.debug('Setting up proactive token refresh interval');
            refreshIntervalRef.current = setInterval(async () => {
              try {
                logger.auth.debug('Proactive token refresh triggered');
                const { session, error } = await supabaseAuthService.refreshSession();
                if (error) {
                  logger.auth.error('Proactive token refresh failed', { error });
                } else if (session) {
                  logger.auth.debug('Proactive token refresh successful', {
                    expiresAt: new Date(session.expires_at! * 1000).toISOString(),
                  });
                }
              } catch (error) {
                logger.auth.error('Proactive token refresh error', { error });
              }
            }, 300000); // Refresh every 5 minutes instead of 50 seconds
          }

          try {
            logger.auth.debug('SIGNED_IN event - fetching user data');
            await fetchUserDataWithCache();
          } catch (error) {
            logger.auth.error('Failed to fetch user data after auth change', { error, event });
            // Don't logout on error - user might still be authenticated
            // Just reset to not authenticated state
            resetAuthState();
          } finally {
            // ALWAYS set initializing to false after handling
            logger.auth.debug('Finally block: Setting initializing to false', { event });
            setInitializing(false);
            clearTimeout(initTimeout);
            isHandlingAuthRef.current = false;
            logger.auth.debug('Finally block: Done', { event });
          }
        } else {
          // No session - handle all events that might have null session
          logger.auth.debug('No session for auth event', { event });
          // Just reset the auth state without triggering Supabase signOut
          resetAuthState();
          logger.auth.debug('Reset auth state, setting initializing to false', { event });
          setInitializing(false);
          clearTimeout(initTimeout);
        }
      } else if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        // For token refresh and user updates, we don't need to call /auth/me
        // Just invalidate the cache so it gets refreshed on next use
        logger.auth.debug('Token refreshed or user updated - invalidating cache', { event });
        invalidateUserData();
        setInitializing(false);
        clearTimeout(initTimeout);
      } else if (event === 'SIGNED_OUT') {
        logger.auth.debug('SIGNED_OUT event - resetting auth state');
        // Clear refresh interval on sign out
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
        resetAuthState();
        removeUserData(); // Clear cached user data
        setInitializing(false);
        clearTimeout(initTimeout);
        isHandlingAuthRef.current = false;
      }
    });

    // Cleanup subscription on unmount
    return () => {
      mounted = false;
      unsubscribe();
      clearTimeout(initTimeout);
      // Clear refresh interval on unmount
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Zustand functions are stable references

  return <>{children}</>;
};
